import { useState, useCallback } from 'react'
import { Head } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import PageHeader from '@/components/ui/PageHeader'
import Notification from '@/components/ui/Notification'
import DataTable, { useFlash, usePagination, type DataTableColumn } from '@/components/ui/DataTable'
import { FormSelect, FormInput } from '@/components/ui/form'
import Button from '@/components/ui/Button'
import Pagination from '@/components/ui/Pagination'
import { Badge } from '@/components/catalyst/badge'
import { Dialog, DialogTitle, DialogBody, DialogActions } from '@/components/catalyst/dialog'
import { TrashIcon, CreditCardIcon } from '@heroicons/react/24/outline'

interface TermRow { id: number; content: string }
interface Props { terms: TermRow[] }
const PAGE_SIZES = [30, 50, 100, 200]

export default function CustomerPaymentTermsIndex({ terms: initial }: Props) {
  const [terms, setTerms] = useState(initial)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formContent, setFormContent] = useState('')
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [notifMessage, setNotifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)
  const { flashRow } = useFlash()
  const csrfToken = typeof document !== 'undefined' ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '' : ''
  const notify = useCallback((msg: string) => { setNotifMessage(msg); setShowNotif(true) }, [])
  const pageSizeOptions = PAGE_SIZES.map(n => ({ value: String(n), label: `${n} per page` }))
  const { pageSize, setPageSize, currentPage, setCurrentPage, totalPages, paginatedRows, totalCount } = usePagination(terms)

  const handleCreate = async () => {
    if (!formContent.trim()) { setFormErrors(['Content is required']); return }
    setFormErrors([]); setSubmitting(true)
    try {
      const res = await fetch('/inertia_customer_payment_terms.json', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ payment_term: { content: formContent.trim() } }),
      })
      if (res.ok) { const data = await res.json(); setTerms(prev => [data, ...prev]); setDialogOpen(false); setFormContent(''); notify('Saved:Payment term created') }
      else { const err = await res.json().catch(() => ({})); setFormErrors(err.errors || ['Failed']) }
    } catch { setFormErrors(['Network error']) }
    setSubmitting(false)
  }

  const deleteTerm = useCallback(async (id: number) => {
    if (!confirm('Delete this payment term?')) return
    try {
      const res = await fetch(`/inertia_customer_payment_terms/${id}.json`, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } })
      if (res.ok) { setTerms(prev => prev.filter(t => t.id !== id)); notify('Deleted:Payment term removed') }
    } catch { /* silent */ }
  }, [csrfToken, notify])

  const columns: DataTableColumn<TermRow>[] = [
    { key: 'content', label: 'Payment Term', render: row => <span className="font-medium">{row.content}</span>, sortValue: row => row.content, filterValue: row => row.content },
  ]

  return (
    <>
      <Head title="Customer Payment Terms" />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />
      <PageHeader title="Customer Payment Terms" icon={<CreditCardIcon className="h-5 w-5" />} actions={
        <div className="flex items-center gap-2">
          <div className="w-36"><FormSelect value={String(pageSize)} onChange={v => { setPageSize(Number(v)); setCurrentPage(0) }} options={pageSizeOptions} /></div>
          <Button onClick={() => { setFormContent(''); setFormErrors([]); setDialogOpen(true) }}>+ New Term</Button>
        </div>
      } />
      <div className="mb-2"><Badge color="zinc"><span className="text-xs font-medium">{totalCount} terms</span></Badge></div>
      <DataTable rows={paginatedRows} columns={columns} rowKey={row => row.id} flashRowId={flashRow?.id ?? null} flashStatus={flashRow?.status ?? null} emptyText="No payment terms" filteredEmptyText="No terms match your filters"
        actions={row => (
          <div className="flex items-center justify-end gap-1">
            <button onClick={() => deleteTerm(row.id)} className="rounded p-1 text-zinc-400 hover:text-red-600 dark:text-zinc-500 dark:hover:text-red-400" title="Delete"><TrashIcon className="h-4 w-4" /></button>
          </div>
        )}
      />
      <Pagination currentPage={currentPage} totalPages={totalPages} pageSize={pageSize} totalCount={totalCount} onPageChange={setCurrentPage} />
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>New Customer Payment Term</DialogTitle>
        <DialogBody>
          {formErrors.length > 0 && <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2 dark:border-red-800 dark:bg-red-900/20"><ul className="list-disc pl-4 text-xs text-red-700 dark:text-red-400">{formErrors.map((e, i) => <li key={i}>{e}</li>)}</ul></div>}
          <div><label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Content *</label><FormInput value={formContent} onChange={e => setFormContent((e.target as HTMLInputElement).value)} placeholder="Payment term..." autoFocus /></div>
        </DialogBody>
        <DialogActions><Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button><Button disabled={submitting} onClick={handleCreate}>{submitting ? 'Creating...' : 'Create'}</Button></DialogActions>
      </Dialog>
    </>
  )
}

CustomerPaymentTermsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
